import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { TwilioConfig } from '@marnic/shared';

const SENTINEL = '••••••••';

type FormState = TwilioConfig;

const EMPTY: FormState = {
  accountSid: '', authToken: '', apiKeySid: '', apiKeySecret: '',
  twimlAppSid: '', defaultCallerId: '', publicApiUrl: '', region: '',
};

export function TwilioConfigSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const configQuery = useQuery({
    queryKey: ['system-config-twilio'],
    queryFn: () => api.get<TwilioConfig>('/system-config/twilio'),
  });

  useEffect(() => {
    if (configQuery.data) setForm(configQuery.data);
  }, [configQuery.data]);

  function handleChange(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleSecretFocus(field: keyof FormState) {
    return () => {
      if (form[field] === SENTINEL) setForm((f) => ({ ...f, [field]: '' }));
    };
  }

  function handleSecretBlur(field: keyof FormState) {
    return () => {
      if (form[field] === '' && configQuery.data?.[field] === SENTINEL) {
        setForm((f) => ({ ...f, [field]: SENTINEL }));
      }
    };
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg(null);
    try {
      await api.patch('/system-config/twilio', form);
      queryClient.invalidateQueries({ queryKey: ['system-config-twilio'] });
      setSaveMsg({ ok: true, text: 'Saved successfully.' });
    } catch {
      setSaveMsg({ ok: false, text: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestMsg(null);
    try {
      const res = await api.post<{ ok: boolean; accountName?: string; error?: string }>(
        '/system-config/twilio/test',
      );
      const media = (res as {
        recordingMedia?: { ok: boolean; status: number; auth: string; detail: string } | null;
      }).recordingMedia;
      const mediaText = media
        ? media.ok
          ? ` Recording media OK via ${media.auth}.`
          : ` Recording media failed (${media.status}) via ${media.auth}.`
        : '';
      setTestMsg({
        ok: res.ok,
        text: res.ok ? `Connected: ${res.accountName}.${mediaText}` : (res.error ?? 'Connection failed'),
      });
    } catch {
      setTestMsg({ ok: false, text: 'Request failed.' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <section className="card p-4 space-y-4">
      <header className="flex items-center justify-between border-b border-ink-200 pb-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">Twilio Configuration</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Credentials saved here take effect immediately — no restart required.
          </p>
        </div>
      </header>

      {configQuery.isLoading && (
        <p className="text-sm text-ink-400">Loading…</p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Account SID</label>
          <input className="input" value={form.accountSid} onChange={handleChange('accountSid')} placeholder="ACxxxxxxxx" />
        </div>
        <div>
          <label className="label">Auth Token</label>
          <input
            className="input"
            type="password"
            value={form.authToken}
            onChange={handleChange('authToken')}
            onFocus={handleSecretFocus('authToken')}
            onBlur={handleSecretBlur('authToken')}
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label">API Key SID</label>
          <input className="input" value={form.apiKeySid} onChange={handleChange('apiKeySid')} placeholder="SKxxxxxxxx" />
        </div>
        <div>
          <label className="label">API Key Secret</label>
          <input
            className="input"
            type="password"
            value={form.apiKeySecret}
            onChange={handleChange('apiKeySecret')}
            onFocus={handleSecretFocus('apiKeySecret')}
            onBlur={handleSecretBlur('apiKeySecret')}
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label">TwiML App SID</label>
          <input className="input" value={form.twimlAppSid} onChange={handleChange('twimlAppSid')} placeholder="APxxxxxxxx" />
        </div>
        <div>
          <label className="label">Default Caller ID</label>
          <input className="input" value={form.defaultCallerId} onChange={handleChange('defaultCallerId')} placeholder="+441234567890" />
        </div>
        <div>
          <label className="label">Public API URL</label>
          <input className="input" value={form.publicApiUrl} onChange={handleChange('publicApiUrl')} placeholder="https://your-ngrok-url.ngrok.io" />
        </div>
        <div>
          <label className="label">Region</label>
          <input className="input" value={form.region} onChange={handleChange('region')} placeholder="ie1  (leave blank for US default)" />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-secondary" onClick={handleTest} disabled={testing}>
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
        {saveMsg && (
          <p className={`text-sm ${saveMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {saveMsg.text}
          </p>
        )}
        {testMsg && (
          <p className={`text-sm ${testMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {testMsg.text}
          </p>
        )}
      </div>
    </section>
  );
}
